import type { FieldDef, NodeType } from '@dispositio/core';
import { useEffect, useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { isCoreField } from '../../lib/fieldSuggestions';
import { useStoryEnv } from '../../state/storyEnv';

/**
 * „⋯“ an einem Feld: umbenennen oder (außer den festen Feldern Name, Kurzbeschreibung, Bild) aus der Klasse entfernen. Beides gilt
 * für alle Einträge der Klasse; `plural` nennt sie in der Rückfrage (null bei Klassen mit genau einem Eintrag, etwa der Welt).
 */
export function FieldMenu({ type, def, plural }: { type: NodeType; def: FieldDef; plural: string | null }) {
  const { actions } = useStoryEnv();
  const [open, setOpen] = useState(false);
  /** null: Menü; sonst der neue Name, der gerade eingegeben wird */
  const [label, setLabel] = useState<string | null>(null);
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (host.current && !host.current.contains(e.target as Node)) {
        setOpen(false);
        setLabel(null);
      }
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    setLabel(null);
  };
  const rename = async (): Promise<void> => {
    const next = (label ?? '').trim();
    close();
    if (next && next !== def.label) await actions.updateType(type.id, { fields: type.fields.map((f) => (f.key === def.key ? { ...f, label: next } : f)) });
  };
  const remove = async (): Promise<void> => {
    close();
    if (!confirm(de.design.fieldRemoveConfirm(def.label, plural))) return;
    await actions.updateType(type.id, { fields: type.fields.filter((f) => f.key !== def.key) });
  };

  return (
    <div className="field-menu-host" ref={host}>
      <button
        type="button"
        className="btn ghost icon field-menu-btn"
        aria-label={de.design.fieldMenu(def.label)}
        title={de.design.fieldMenu(def.label)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        data-testid={`field-menu-${def.key}`}
      >
        ⋯
      </button>
      {open && (
        <div className="field-menu" role="menu" aria-label={de.design.fieldMenu(def.label)}>
          {label === null ? (
            <>
              <button type="button" role="menuitem" className="field-menu-item" onClick={() => setLabel(def.label)} data-testid="field-rename">
                {de.design.fieldRename}
              </button>
              {!isCoreField(type, def) && (
                <button type="button" role="menuitem" className="field-menu-item danger" onClick={() => void remove()} data-testid="field-remove">
                  {de.design.fieldRemove}
                </button>
              )}
            </>
          ) : (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                void rename();
              }}
            >
              <input
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') return;
                  e.stopPropagation(); // nur das Menü schließen, nicht das Fenster
                  close();
                }}
                aria-label={de.design.fieldName}
                maxLength={120}
                data-testid="field-rename-input"
              />
              <button type="submit" className="btn small primary" data-testid="field-rename-ok">
                {de.common.ok}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
